import type { InputHTMLAttributes, ReactNode } from "react";
import { JcLogo } from "./jc-logo";
import { forwardRef, useState } from "react";

/**
 * v13 AUTH — the first screen a client ever sees.
 *
 * ⛔ This stopped being an internal page on 8 Sep, when Harmless Harvest's own
 * people got accounts. Mike Fitzgerald opens `/login` before he opens anything
 * else, so it carries the house style or the product's first impression is the
 * OLD product.
 *
 * ⭐ THE PHOTOGRAPH IS THE LEFT HALF, not a background wash. It is the same desk
 * the left rail carries once you are inside, so signing in reads as walking into
 * the same room rather than through a different door. On a narrow screen the
 * photo drops away entirely — a 40% band of cropped mug is not a design.
 */

export function AuthShell({
  title,
  sub,
  children,
  foot,
}: {
  title: string;
  /** One line under the title. Null when the title says it all. */
  sub?: string | null;
  children: ReactNode;
  foot?: ReactNode;
}) {
  return (
    <div
      className="yl-root flex min-h-screen"
      style={{ background: "var(--yl-ground)" }}
    >
      {/* ── the room ──────────────────────────────────────────────────
          ⛔⛔ NATURAL SIZE, ANCHORED TO THE FLOOR — NEVER `cover`.
          `background-size: cover` scaled a 409×493 photograph up to fill a
          ~550×910 column, so the mug filled half the screen and went soft.
          Haroon has caught me upscaling this exact asset before. The photo is
          409px wide, full stop; it sits on the cream ground at the bottom-left
          and the rest of the panel is simply the room's light. */}
      <div
        aria-hidden
        className="relative hidden lg:block"
        style={{
          width: 409,
          flexShrink: 0,
          backgroundColor: "var(--yl-ground)",
          backgroundImage: "url(/branding/jumpcannon/rail-still-life-v2.png)",
          backgroundSize: "305px auto" /* never upscaled (MC 4988) */,
          backgroundPosition: "left bottom",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* ── the form ──────────────────────────────────────────────────── */}
      <div
        className="flex flex-1 items-center justify-center"
        style={{ padding: "40px 28px" }}
      >
        <div style={{ width: "100%", maxWidth: 392 }}>
          <div style={{ marginBottom: 30 }}>
            <JcLogo size={24} />
          </div>

          <h1
            className="text-[var(--yl-ink)]"
            style={{
              fontFamily: "var(--yl-serif)",
              fontSize: 30,
              fontWeight: 600,
              lineHeight: 1.15,
              letterSpacing: "var(--yl-ls-h1)",
            }}
          >
            {title}
          </h1>
          {sub ? (
            <p
              className="text-[var(--yl-muted)]"
              style={{
                fontSize: "var(--yl-fs-sub)",
                marginTop: 9,
                lineHeight: 1.55,
              }}
            >
              {sub}
            </p>
          ) : null}

          <div style={{ marginTop: 26 }}>{children}</div>

          {foot ? <div style={{ marginTop: 22 }}>{foot}</div> : null}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   FIELDS
   ══════════════════════════════════════════════════════════════════════════ */

const control = {
  width: "100%",
  background: "var(--yl-panel)",
  border: "1px solid var(--yl-rule)",
  borderRadius: "var(--yl-radius-control)",
  padding: "11px 13px",
  fontSize: "var(--yl-fs-row)",
  color: "var(--yl-ink-row)",
  outline: "none",
} as const;

/**
 * ⛔ `forwardRef` is load-bearing — `react-hook-form`'s `register()` finds the
 * DOM node through the ref, and a plain function component swallows it SILENTLY:
 * the field renders, accepts typing, and never validates. Same trap that left
 * Settings › General with empty boxes on 8 Sep.
 */
export const AuthInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function AuthInput({ invalid, style, ...rest }, ref) {
  return (
    <input
      ref={ref}
      {...rest}
      style={{
        ...control,
        borderColor: invalid ? "var(--yl-clay)" : "var(--yl-rule)",
        ...style,
      }}
    />
  );
});

/** Password, with a reveal that says what it will do rather than an eye. */
export const AuthPassword = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function AuthPassword({ invalid, style, ...rest }, ref) {
  const [shown, setShown] = useState(false);
  return (
    <span className="relative block">
      <input
        ref={ref}
        {...rest}
        type={shown ? "text" : "password"}
        style={{
          ...control,
          borderColor: invalid ? "var(--yl-clay)" : "var(--yl-rule)",
          paddingRight: 58,
          ...style,
        }}
      />
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        className="absolute uppercase text-[var(--yl-faint)] hover:text-[var(--yl-clay)]"
        style={{
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: "var(--yl-fs-eyebrow)",
          letterSpacing: "var(--yl-ls-eyebrow)",
          fontWeight: 600,
        }}
      >
        {shown ? "Hide" : "Show"}
      </button>
    </span>
  );
});

export function AuthField({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="block" style={{ marginBottom: 16 }}>
      <span
        className="mb-1.5 block font-semibold uppercase text-[var(--yl-faint)]"
        style={{
          fontSize: "var(--yl-fs-table-head)",
          letterSpacing: "var(--yl-ls-table-head)",
        }}
      >
        {label}
      </span>
      {children}
      {/* ⛔ Reserved height, so an error appearing does not shove the button
          down under the reader's cursor mid-click. */}
      <span
        className="mt-1.5 block"
        style={{ minHeight: 17 }}
        aria-live="polite"
      >
        {error ? (
          <span
            className="text-[var(--yl-clay)]"
            style={{ fontSize: "var(--yl-fs-small)" }}
          >
            {error}
          </span>
        ) : hint ? (
          <span
            className="text-[var(--yl-faint)]"
            style={{ fontSize: "var(--yl-fs-small)" }}
          >
            {hint}
          </span>
        ) : null}
      </span>
    </label>
  );
}

/** The one big action on the page. */
export function AuthButton({
  children,
  ...rest
}: { children: ReactNode } & Record<string, unknown>) {
  return (
    <button
      {...rest}
      className="w-full border transition-colors"
      style={{
        background: "var(--action)",
        borderColor: "var(--yl-pine)",
        color: "var(--yl-on-dark)",
        borderRadius: "var(--yl-radius-control)",
        padding: "12px 18px",
        fontSize: "var(--yl-fs-sub)",
        fontWeight: 600,
        cursor: rest.disabled ? "default" : "pointer",
        opacity: rest.disabled ? 0.55 : 1,
      }}
    >
      {children}
    </button>
  );
}

/**
 * A message the page must not bury.
 * ⛔ `tone="clay"` is a REFUSAL (wrong password, expired link); `tone="pine"` is
 * a confirmation. Never use clay for a confirmation because it is more visible.
 */
export function AuthNote({
  children,
  tone = "clay",
}: {
  children: ReactNode;
  tone?: "clay" | "pine";
}) {
  const c =
    tone === "pine"
      ? { bg: "#eef3ec", rule: "#d3e2d6", ink: "var(--yl-pine)" }
      : {
          bg: "var(--yl-warn-tint)",
          rule: "var(--yl-warn-rule)",
          ink: "var(--yl-clay)",
        };
  return (
    <div
      role="status"
      style={{
        background: c.bg,
        border: `1px solid ${c.rule}`,
        borderRadius: "var(--yl-radius-control)",
        color: c.ink,
        padding: "10px 13px",
        fontSize: "var(--yl-fs-body)",
        lineHeight: 1.5,
        marginBottom: 16,
      }}
    >
      {children}
    </div>
  );
}

/** A quiet link under the form. */
export function AuthLink({
  children,
  ...rest
}: { children: ReactNode } & Record<string, unknown>) {
  return (
    <span
      {...rest}
      className="text-[var(--yl-clay)] hover:text-[var(--yl-clay-hover)]"
      style={{
        fontSize: "var(--yl-fs-body)",
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      {children}
    </span>
  );
}
